import { chartDarkOptions, chartLightOptions, defaultColors } from './constant';
import { CustomLineSeries } from './custom-line-series';
import { TooltipPanelPrimitive } from './plugin/tooltip-panel';
import { TooltipPrimitive } from './plugin/tooltip-series';
import { LineSeriesItem } from './types';
import { createLogoWatermarkPlugin, margeChartOptions } from './utils';
import useLocale from '@/client/hooks/use-locale';
import { useTheme } from '@/client/hooks/use-theme';
import { cn } from '@/client/utils';
import { formatDate, formatRate } from '@/client/utils/format';
import { ChartOptions, createChart, DeepPartial, IChartApi, MouseEventParams, Time } from 'lightweight-charts';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

type LiveDot = { x: number; y: number; color: string };

export type LineChartProps = {
  children?: ReactNode;
  className?: string;
  showLogo?: boolean;
  options?: Omit<DeepPartial<ChartOptions>, 'width' | 'height'>;
  series: LineSeriesItem[];
  max?: number;
  min?: number;
  onCrosshairMove?: (params: MouseEventParams<Time>, chart: IChartApi) => void;
};

export const LineChart = ({
  children,
  className,
  showLogo = false,
  options,
  series,
  onCrosshairMove,
  min: minProp,
  max: maxProp,
}: LineChartProps) => {
  const { locale } = useLocale();
  const { theme } = useTheme();
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const chart = useRef<IChartApi | null>(null);
  // Live pulsing dot at the end of each visible line (Polymarket-style).
  const [liveDots, setLiveDots] = useState<LiveDot[]>([]);
  const updateDotsRef = useRef<() => void>(() => {});

  const { min, max } = useMemo(() => {
    let minValue = Number.MAX_SAFE_INTEGER;
    let maxValue = Number.MIN_SAFE_INTEGER;
    series.forEach(item => {
      if (!item.data || !item.data.length) return;
      item.data.forEach(point => {
        if (typeof point.value === 'number') {
          minValue = Math.min(minValue, point.value);
          maxValue = Math.max(maxValue, point.value);
        }
      });
    });
    if (minValue === Number.MAX_SAFE_INTEGER) minValue = minProp ?? 0;
    if (maxValue === Number.MIN_SAFE_INTEGER) maxValue = maxProp ?? 100;
    return { min: minValue, max: maxValue };
  }, [series, minProp, maxProp]);

  // handleCrosshairMove
  const handleCrosshairMove = useCallback(
    (params: MouseEventParams<Time>) => {
      if (!chart.current) return;
      onCrosshairMove?.(params, chart.current);
    },
    [onCrosshairMove]
  );

  // handle size
  const handleResize = () => {
    requestAnimationFrame(() => {
      if (!chartContainerRef.current || !chart.current) return;
      const width = chartContainerRef.current.clientWidth;
      const height = chartContainerRef.current.clientHeight;
      chart.current.resize(width, height);
      chart.current.timeScale().fitContent();
      updateDotsRef.current();
    });
  };
  useEffect(() => {
    window?.addEventListener('resize', handleResize);
    return () => window?.removeEventListener('resize', handleResize);
  }, []);

  // handle chart info
  useEffect(() => {
    if (!chartContainerRef.current) return;
    const width = chartContainerRef.current.clientWidth;
    const height = chartContainerRef.current.clientHeight;
    const currentOptions = margeChartOptions(
      {
        localization: {
          timeFormatter: (value: Time) => `${formatDate(value, locale)}`,
          priceFormatter: (value: number) => `${formatRate(value, 0)}`,
        },
        grid: {
          horzLines: {
            visible: true,
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.11)' : 'rgba(0, 0, 0, 0.11)',
          },
        },
        ...options,
        width,
        height,
      },
      theme
    );
    chart.current = createChart(chartContainerRef.current, currentOptions);
    const panel = chart.current.panes()[0];
    if (showLogo) panel.attachPrimitive(createLogoWatermarkPlugin());
    panel.attachPrimitive(
      new TooltipPanelPrimitive({
        lineColor: theme == 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(19, 21, 23, 0.08)',
        lineTextColor: theme == 'dark' ? 'rgba(255, 255, 255, 0.639)' : '#323436 ',
        panelBackgroundColor: theme == 'dark' ? '#323436' : 'rgba(255, 255, 255, 1)',
        panelBorderColor: theme == 'dark' ? 'rgba(255, 255, 255, 0.10)' : 'rgba(54, 56, 59, 0.10)',
      })
    );
    // Track the last point of each visible series for the live dot overlay.
    const dotSources: { series: ReturnType<IChartApi['addCustomSeries']>; time: Time; value: number; color: string }[] = [];
    for (let i = 0; i < series.length; i++) {
      const { label, data, color, visible } = series[i];
      const lineSeries = chart.current.addCustomSeries(new CustomLineSeries(), {
        label,
        color: color ?? defaultColors[i],
        lineColor: color ?? defaultColors[i],
        customColor: theme === 'dark' ? chartDarkOptions.lineColor : chartLightOptions.lineColor,
        lineWidth: 2,
        lastValueVisible: false,
        priceLineVisible: false,
        visible,
        autoscaleInfoProvider: () => ({
          priceRange: {
            minValue: min * 0.5,
            maxValue: max * 1.1,
          },
        }),
        ...options,
      });
      lineSeries.setData(data);
      if (visible !== false && data.length) {
        const last = data[data.length - 1] as { time: Time; value?: number };
        if (typeof last.value === 'number') {
          dotSources.push({ series: lineSeries, time: last.time, value: last.value, color: color ?? defaultColors[i] });
        }
      }
      const tooltipPrimitive = new TooltipPrimitive({ label });
      tooltipPrimitive.activeRange().subscribe(customRange => {
        if (customRange) {
          lineSeries.applyOptions({ customRange });
        } else if (lineSeries.options().customRange.from !== 0) {
          lineSeries.applyOptions({ customRange: { from: 0, to: 0 } });
        }
      });
      lineSeries.attachPrimitive(tooltipPrimitive);
    }
    chart.current.timeScale().fitContent();
    chart.current.subscribeCrosshairMove(handleCrosshairMove);
    chart.current.timeScale().scrollToRealTime();

    // Live dot positions: recompute from the last point of each visible series.
    const updateDots = () => {
      if (!chart.current) return;
      const ts = chart.current.timeScale();
      const next: LiveDot[] = [];
      for (const s of dotSources) {
        const x = ts.timeToCoordinate(s.time);
        const y = s.series.priceToCoordinate(s.value);
        if (x == null || y == null) continue;
        next.push({ x, y, color: s.color });
      }
      setLiveDots(next);
    };
    updateDotsRef.current = updateDots;
    updateDots();
    const ts = chart.current.timeScale();
    ts.subscribeVisibleTimeRangeChange(updateDots);
    return () => {
      ts.unsubscribeVisibleTimeRangeChange(updateDots);
      chart.current?.unsubscribeCrosshairMove(handleCrosshairMove);
      chart.current?.remove();
      chart.current = null;
    };
  }, [handleCrosshairMove, locale, max, min, options, series, showLogo, theme]);
  return (
    <div ref={chartContainerRef} className={cn('size-full pos-relative', className)}>
      {liveDots.map((dot, i) => (
        <span
          key={i}
          className="chart-live-dot"
          style={{ left: dot.x, top: dot.y, ['--dot-color' as string]: dot.color }}
        />
      ))}
      {children}
    </div>
  );
};
